import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person,
  School,
  AdminPanelSettings,
  ExitToApp,
  Book,
  Login,
  PersonAdd,
  DeveloperMode,
  Help,
  Event,
  PlayArrow,
  Wallpaper,
  CalendarToday,
  ListAlt,
  ArrowDropDown,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import FloatingAdmin from './FloatingAdmin';
import getAppConfig from '../config/app-config';

// Costanti
const HIDE_AUTH_BUTTONS_PAGES = [
  '/login',
  '/register',
  '/verify-email',
  '/registration-success',
  '/reset-password',
  '/forgot-password',
  '/membership-checkout',
  '/membership-callback',
];

const MENU_ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'right',
};

const MENU_TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'right',
};

const DROPDOWN_MENU_ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'left',
};

const DROPDOWN_MENU_TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'left',
};

const TUTORIAL_BUTTON_STYLES = {
  borderColor: 'white',
  color: 'white',
  '&:hover': {
    borderColor: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
};

// Componenti helper
const Logo = ({ logoUrl, brandName, onClick, size = 'large' }) => {
  const logoHeight = size === 'large' ? '40px' : '24px';
  const variant = size === 'large' ? 'h5' : 'h6';

  return (
    <Box
      sx={{
        flexGrow: size === 'large' ? 1 : 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&:hover': {
          opacity: 0.8,
        },
      }}
      onClick={onClick}
    >
      <img
        src={logoUrl}
        alt='Logo'
        style={{
          height: logoHeight,
          width: 'auto',
        }}
      />
      <Typography variant={variant} component='div' sx={{ fontWeight: 'bold' }} color={size === 'small' ? 'primary' : 'inherit'}>
        {brandName}
      </Typography>
    </Box>
  );
};

const TutorialButton = ({ onNavigate, variant = 'text' }) => (
  <Button
    color='inherit'
    onClick={() => onNavigate('/tutorial')}
    startIcon={<Help />}
    variant={variant}
    sx={variant === 'outlined' ? TUTORIAL_BUTTON_STYLES : undefined}
  >
    Tutorial
  </Button>
);

const MobileNavItem = ({ item, onNavigate, expandedDropdowns, onToggleDropdown }) => {
  if (item.hasDropdown) {
    const isExpanded = expandedDropdowns[item.text] || false;
    return (
      <React.Fragment key={item.text}>
        <ListItemButton onClick={() => onToggleDropdown(item.text)}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.dropdownItems.map((dropdownItem) => (
              <ListItemButton
                key={dropdownItem.text}
                sx={{ pl: 4 }}
                onClick={() => onNavigate(dropdownItem.path)}
              >
                <ListItemIcon>{dropdownItem.icon}</ListItemIcon>
                <ListItemText primary={dropdownItem.text} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  }

  return (
    <ListItemButton key={item.text} onClick={() => onNavigate(item.path)}>
      <ListItemIcon>{item.icon}</ListItemIcon>
      <ListItemText primary={item.text} />
    </ListItemButton>
  );
};

const DesktopNavItem = ({ item, onNavigate, onReservationsMenuOpen, onCoursesMenuOpen }) => {
  if (item.hasDropdown) {
    const handleClick = item.text === 'Prenotazioni' ? onReservationsMenuOpen : onCoursesMenuOpen;
    return (
      <Button
        key={item.text}
        color='inherit'
        onClick={handleClick}
        startIcon={item.icon}
        endIcon={<ArrowDropDown />}
      >
        {item.text}
      </Button>
    );
  }

  return (
    <Button
      key={item.text}
      color='inherit'
      onClick={() => onNavigate(item.path)}
      startIcon={item.icon}
    >
      {item.text}
    </Button>
  );
};

const MobileDrawer = ({
  config,
  brandName,
  isAuthenticated,
  user,
  navItems,
  expandedDropdowns,
  onNavigate,
  onToggleDropdown,
  onLogout,
  shouldShowAuthButtons,
}) => {
  return (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Logo logoUrl={config.LOGO_URL} brandName={brandName} onClick={() => onNavigate('/')} size="small" />
        {isAuthenticated && (
          <Typography variant='body2' color='text.secondary'>
            {user?.name} {user?.surname}
          </Typography>
        )}
      </Box>
      <List>
        {navItems.map((item) => (
          <MobileNavItem
            key={item.text}
            item={item}
            onNavigate={onNavigate}
            expandedDropdowns={expandedDropdowns}
            onToggleDropdown={onToggleDropdown}
          />
        ))}

        {config.SHOW_TUTORIAL && (
          <>
            <Divider />
            <ListItemButton onClick={() => onNavigate('/tutorial')}>
              <ListItemIcon>
                <Help />
              </ListItemIcon>
              <ListItemText primary='Tutorial' />
            </ListItemButton>
          </>
        )}

        {isAuthenticated ? (
          <>
            <Divider />
            <ListItemButton onClick={() => onNavigate('/profile')}>
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <ListItemText primary='Profilo' />
            </ListItemButton>
            <ListItemButton onClick={onLogout}>
              <ListItemIcon>
                <ExitToApp />
              </ListItemIcon>
              <ListItemText primary='Logout' />
            </ListItemButton>
          </>
        ) : shouldShowAuthButtons ? (
          <>
            <Divider />
            <ListItemButton onClick={() => onNavigate('/login')}>
              <ListItemIcon>
                <Login />
              </ListItemIcon>
              <ListItemText primary='Login' />
            </ListItemButton>
            <ListItemButton onClick={() => onNavigate('/register')}>
              <ListItemIcon>
                <PersonAdd />
              </ListItemIcon>
              <ListItemText primary='Registrati' />
            </ListItemButton>
          </>
        ) : null}
      </List>
    </Box>
  );
};

const UserAvatar = ({ user, onMenuOpen }) => {
  const getAvatarSrc = () => {
    if (!user?.avatar) return undefined;
    return user.avatar.startsWith('data:image/') ? user.avatar : `/avatars/${user.avatar}`;
  };

  const getInitials = () => {
    if (user?.avatar && user.avatar !== '') return null;
    return user?.name?.charAt(0)?.toUpperCase() || 'U';
  };

  return (
    <IconButton onClick={onMenuOpen} sx={{ color: 'inherit' }}>
      <Avatar
        sx={{
          width: 48,
          height: 48,
          bgcolor: 'secondary.main',
          borderColor: 'white',
          border: 2,
        }}
        src={getAvatarSrc()}
      >
        {getInitials()}
      </Avatar>
    </IconButton>
  );
};

const AuthButtons = ({ onNavigate }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Button color='inherit' onClick={() => onNavigate('/login')} startIcon={<Login />}>
      Login
    </Button>
    <Button
      color='inherit'
      onClick={() => onNavigate('/register')}
      startIcon={<PersonAdd />}
      variant='outlined'
      sx={TUTORIAL_BUTTON_STYLES}
    >
      Registrati
    </Button>
  </Box>
);

const Navbar = () => {
  const config = getAppConfig();

  // Validazione configurazione
  if (!config.BRAND_NAME) {
    throw new Error('BRAND_NAME non è definita nella configurazione.');
  }

  if (!config.LOGO_URL) {
    throw new Error('LOGO_URL non è definita nella configurazione.');
  }

  const { user, logout, isAuthenticated, isProd, isDev } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = !isMobile;
  const isRockdreamsProduction = config.ASSOCIATE === 'rockdreams' && (isProd || isDev); // nascondi tutti gli item di navigazione a parte VIE
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = useState(null);
  const [reservationsMenuAnchorEl, setReservationsMenuAnchorEl] = useState(null);
  const [coursesMenuAnchorEl, setCoursesMenuAnchorEl] = useState(null);
  const [expandedMobileDropdowns, setExpandedMobileDropdowns] = useState({});

  const shouldHideAuthButtons = HIDE_AUTH_BUTTONS_PAGES.includes(location.pathname);
  const shouldShowAuthButtons = !shouldHideAuthButtons;
  const shouldShowAdminTools = !isProd;


  // Handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAdminMenuOpen = (event) => setAdminMenuAnchorEl(event.currentTarget);
  const handleAdminMenuClose = () => setAdminMenuAnchorEl(null);
  const handleReservationsMenuOpen = (event) => setReservationsMenuAnchorEl(event.currentTarget);
  const handleReservationsMenuClose = () => setReservationsMenuAnchorEl(null);
  const handleCoursesMenuOpen = (event) => setCoursesMenuAnchorEl(event.currentTarget);
  const handleCoursesMenuClose = () => setCoursesMenuAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    handleMenuClose();
  };

  const handleMobileToggle = () => setMobileOpen((prev) => !prev);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleMobileDropdownToggle = (itemText) => {
    setExpandedMobileDropdowns((prev) => ({
      ...prev,
      [itemText]: !prev[itemText],
    }));
  };

  // Generazione nav items
  const getNavItems = () => {
    const items = [];

    if (isAuthenticated) {
      if (user?.roles.includes('member') && !isRockdreamsProduction) {
        items.push(
          { text: 'Calendario', path: '/member-calendar', icon: <Event /> },
          {
            text: 'Corsi',
            icon: <Book />,
            hasDropdown: true,
            dropdownItems: [
              { text: 'Lezioni di Prova', path: '/trial-lessons', icon: <PlayArrow /> },
              { text: 'Elenco Corsi', path: '/courses', icon: <Book /> },
              { text: 'Miei Corsi', path: '/my-courses', icon: <School /> },
            ],
          }
        );
      }

      if (user?.roles.includes('teacher') && !isRockdreamsProduction) {
        items.push({ text: 'Area Docente', path: '/teacher', icon: <School /> });
      }

      if (user?.roles.includes('admin')) {
        items.push({ text: 'Amministrazione', path: '/admin', icon: <AdminPanelSettings /> });
      }
    }

    if (config.WITH_WALLS) {
      items.push({ text: 'Vie', path: '/walls', icon: <Wallpaper /> });
    }

    if (config.WITH_RESERVATION && isAuthenticated) {
      const dropdownItems = [];
      
      if (user?.roles.includes('member') && !isRockdreamsProduction) {
        dropdownItems.push(
          { text: 'Prenota', path: '/reservables', icon: <CalendarToday /> },
          { text: 'Le Mie Prenotazioni', path: '/my-reservations', icon: <ListAlt /> }
        );
      } else if (user?.roles.includes('admin')) {
        dropdownItems.push(
          { text: 'Gestisci', path: '/manage-reservations', icon: <AdminPanelSettings /> }
        );
      }
      
      if (dropdownItems.length > 0) {
        items.push({
          text: 'Prenotazioni',
          icon: <CalendarToday />,
          hasDropdown: true,
          dropdownItems,
        });
      }
    }

    return items;
  };

  const navItems = getNavItems();

  // Helper per ottenere dropdown items
  const getDropdownItems = (menuType) => {
    return navItems
      .filter((item) => item.hasDropdown && item.text === menuType)
      .flatMap((item) => item.dropdownItems);
  };

  const renderRightSideContent = () => {
    if (isAuthenticated) {
      return <UserAvatar user={user} onMenuOpen={handleMenuOpen} />;
    }

    if (shouldShowAuthButtons) {
      if (isDesktop) {
        return <AuthButtons onNavigate={handleNavigation} />;
      }
      if (config.SHOW_TUTORIAL) {
        return <TutorialButton onNavigate={handleNavigation} variant="outlined" />;
      }
    }

    return null;
  };

  return (
    <>
      <AppBar position='sticky' sx={{ borderRadius: 0 }}>
        <Toolbar disableGutters>
          <Container maxWidth='lg' sx={{ px: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: { xs: 'space-between', md: 'flex-start' },
              }}
            >
              {isMobile && (
                <IconButton color='inherit' edge='start' onClick={handleMobileToggle} sx={{ mr: 2 }}>
                  <MenuIcon />
                </IconButton>
              )}

              {isDesktop && (
                <Logo
                  logoUrl={config.LOGO_URL}
                  brandName={config.BRAND_NAME}
                  onClick={() => handleNavigation('/')}
                  size="large"
                />
              )}

              {isDesktop && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {config.SHOW_TUTORIAL && (
                    <TutorialButton onNavigate={handleNavigation} />
                  )}

                  {navItems.map((item) => (
                    <DesktopNavItem
                      key={item.text}
                      item={item}
                      onNavigate={handleNavigation}
                      onReservationsMenuOpen={handleReservationsMenuOpen}
                      onCoursesMenuOpen={handleCoursesMenuOpen}
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, md: 2 } }}>
                {shouldShowAdminTools && (
                  <IconButton
                    color='inherit'
                    onClick={handleAdminMenuOpen}
                    sx={{ mr: 1 }}
                    title='Admin Tools'
                  >
                    <DeveloperMode />
                  </IconButton>
                )}
                {renderRightSideContent()}
              </Box>
            </Box>
          </Container>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
      >
        <MenuItem
          onClick={() => {
            handleNavigation('/profile');
            handleMenuClose();
          }}
        >
          <Person sx={{ mr: 1 }} />
          Profilo
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ExitToApp sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Admin Tools Menu */}
      <Menu
        anchorEl={adminMenuAnchorEl}
        open={Boolean(adminMenuAnchorEl)}
        onClose={handleAdminMenuClose}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              maxHeight: 400,
              overflow: 'auto',
            },
          },
        }}
      >
        <FloatingAdmin />
      </Menu>

      {/* Reservations Dropdown Menu */}
      <Menu
        anchorEl={reservationsMenuAnchorEl}
        open={Boolean(reservationsMenuAnchorEl)}
        onClose={handleReservationsMenuClose}
        anchorOrigin={DROPDOWN_MENU_ANCHOR_ORIGIN}
        transformOrigin={DROPDOWN_MENU_TRANSFORM_ORIGIN}
      >
        {getDropdownItems('Prenotazioni').map((dropdownItem) => (
          <MenuItem
            key={dropdownItem.text}
            onClick={() => {
              handleNavigation(dropdownItem.path);
              handleReservationsMenuClose();
            }}
          >
            <ListItemIcon>{dropdownItem.icon}</ListItemIcon>
            <ListItemText primary={dropdownItem.text} />
          </MenuItem>
        ))}
      </Menu>

      {/* Courses Dropdown Menu */}
      <Menu
        anchorEl={coursesMenuAnchorEl}
        open={Boolean(coursesMenuAnchorEl)}
        onClose={handleCoursesMenuClose}
        anchorOrigin={DROPDOWN_MENU_ANCHOR_ORIGIN}
        transformOrigin={DROPDOWN_MENU_TRANSFORM_ORIGIN}
      >
        {getDropdownItems('Corsi').map((dropdownItem) => (
          <MenuItem
            key={dropdownItem.text}
            onClick={() => {
              handleNavigation(dropdownItem.path);
              handleCoursesMenuClose();
            }}
          >
            <ListItemIcon>{dropdownItem.icon}</ListItemIcon>
            <ListItemText primary={dropdownItem.text} />
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        variant='temporary'
        anchor='left'
        open={mobileOpen}
        onClose={handleMobileToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        <MobileDrawer
          config={config}
          brandName={config.BRAND_NAME}
          isAuthenticated={isAuthenticated}
          user={user}
          navItems={navItems}
          expandedDropdowns={expandedMobileDropdowns}
          onNavigate={handleNavigation}
          onToggleDropdown={handleMobileDropdownToggle}
          onLogout={handleLogout}
          shouldShowAuthButtons={shouldShowAuthButtons}
        />
      </Drawer>
    </>
  );
};

export default Navbar;
